import { useLingui } from '@lingui/react/macro'
import { getDocChatTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type { PreviewTrendHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import type { DocChatTurn } from 'features/knowledgeDocumentViewer/types'
import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useStickToBottom } from 'utils/useStickToBottom'

import { timelineItemEnter } from './docChatStyles'
import {
  DocChatTimelineTurnRows,
  TimelineConnector,
} from './DocChatTimelineRows'
import { DocChatTurnRows } from './DocChatTurnRows'
import { JumpToLatestButton } from './JumpToLatestButton'
import type {
  NavigateDocumentHandler,
  PreviewDocumentHandler,
  PreviewImageHandler,
  PreviewInspectionHandler,
  PreviewRevisionHandler,
} from './previewDocumentHandler'

type DocChatTimelineVariant = 'header' | 'sidePanel'

const TimelineWrapper = styled.div<{ $variant: DocChatTimelineVariant }>`
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${(props) =>
    props.$variant === 'sidePanel'
      ? `
        flex: 1;
        min-height: 0;
      `
      : ''}
`

const Timeline = styled.div<{ $variant: DocChatTimelineVariant }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  ${(props) =>
    props.$variant === 'header'
      ? `
        max-height: 9.25rem;
        padding: 0.25rem 0.5rem 0.5rem;
        scrollbar-gutter: stable;
      `
      : `
        flex: 1;
        min-height: 0;
        padding: 0.75rem 0 0.75rem 0.25rem;
        scrollbar-gutter: stable;
        scrollbar-width: thin;
      `}
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: ${(props) =>
      props.$variant === 'sidePanel' ? '0.25rem' : '0.5rem'};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 9999rem;
    background: var(--border);
  }
`

const JumpToLatestButtonSlot = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 45;
`

const TimelineContent = styled.div<{ $variant: DocChatTimelineVariant }>`
  min-width: 0;
  position: relative;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  ${(props) =>
    props.$variant === 'sidePanel'
      ? `
        gap: 1rem;
        padding-right: 0.75rem;
      `
      : `
        &::before {
          content: '';
          position: absolute;
          top: 0.75rem;
          bottom: 0.75rem;
          left: 0.75rem;
          width: 0.0625rem;
          transform: translateX(-50%);
          border-radius: 9999rem;
          background: var(--border);
        }
      `}
`

const TurnBlock = styled.div<{ $animate: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  scroll-margin-block: 0.25rem;
  ${(props) =>
    props.$animate
      ? `
        animation: ${timelineItemEnter} 180ms ease-out both;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
        }
      `
      : ''}
`

type DocChatTimelineProps =
  | {
      turns: DocChatTurn[]
      variant?: 'header'
    }
  | {
      onPreviewDocument: PreviewDocumentHandler
      onPreviewTrend?: PreviewTrendHandler
      onPreviewImage?: PreviewImageHandler
      onPreviewInspection?: PreviewInspectionHandler
      onNavigateDocument?: NavigateDocumentHandler
      onPreviewRevision?: PreviewRevisionHandler
      conversationId?: string | null
      turns: DocChatTurn[]
      variant: 'sidePanel'
    }

export function DocChatTimeline(
  props: DocChatTimelineProps
): React.ReactElement {
  const { t } = useLingui()
  const { turns, variant = 'header' } = props
  const timelineContentRef = useRef<HTMLDivElement>(null)
  const isSidePanel = variant === 'sidePanel'

  const { scrollRef, onScroll, isPinned, hasNewContent, scrollToLatest } =
    useStickToBottom({
      contentRef: timelineContentRef,
      scrollDeps: [turns],
      resetWhenEmpty: turns.length === 0,
    })

  const isLastTurnLoading =
    turns.length > 0 &&
    getDocChatTurnStatus(turns[turns.length - 1]) === 'loading'

  // The newest answer's follow-up questions show until the next turn starts.
  const lastTurn = turns.at(-1)
  const suggestionsTurnId =
    isSidePanel && lastTurn && getDocChatTurnStatus(lastTurn) === 'complete'
      ? lastTurn.id
      : undefined

  const latestActionableTurnId = useMemo(() => {
    if (!isSidePanel) return undefined
    for (let index = turns.length - 1; index >= 0; index -= 1) {
      const turn = turns[index]
      const status = getDocChatTurnStatus(turn)
      if (status !== 'loading') {
        return turn.id
      }
    }
    return undefined
  }, [isSidePanel, turns])

  return (
    <TimelineWrapper $variant={variant}>
      <Timeline
        aria-label={t`Docchat conversation`}
        $variant={variant}
        ref={scrollRef}
        role='log'
        onScroll={onScroll}>
        <TimelineContent $variant={variant} ref={timelineContentRef}>
          {turns.map((turn, index) => (
            <React.Fragment key={turn.id}>
              {!isSidePanel && index > 0 && <TimelineConnector />}
              <TurnBlock $animate={!isSidePanel}>
                {props.variant === 'sidePanel' ? (
                  <DocChatTurnRows
                    onPreviewDocument={props.onPreviewDocument}
                    onPreviewTrend={props.onPreviewTrend}
                    onPreviewImage={props.onPreviewImage}
                    onPreviewInspection={props.onPreviewInspection}
                    onNavigateDocument={props.onNavigateDocument}
                    onPreviewRevision={props.onPreviewRevision}
                    conversationId={props.conversationId}
                    showActions={turn.id === latestActionableTurnId}
                    showSuggestions={turn.id === suggestionsTurnId}
                    turn={turn}
                  />
                ) : (
                  <DocChatTimelineTurnRows turn={turn} />
                )}
              </TurnBlock>
            </React.Fragment>
          ))}
        </TimelineContent>
      </Timeline>
      {isSidePanel && !isPinned && hasNewContent && (
        <JumpToLatestButtonSlot>
          <JumpToLatestButton
            isLoading={isLastTurnLoading}
            onClick={scrollToLatest}
          />
        </JumpToLatestButtonSlot>
      )}
    </TimelineWrapper>
  )
}
